import {
    bulkEquipmentCandidateId,
    formatGroupedEquipmentName,
    isStockEquipment,
    maxQuantityForBulkAssignGap,
} from 'src/equipment/stock-equipment'

import {allocationCountForRequirement} from './allocation'
import type {DispatchResource, JobAllocation, JobRequirement, UnassignedJob} from './types'

export {
    bulkEquipmentCandidateId,
    formatGroupedEquipmentName,
    isStockEquipment,
    maxQuantityForBulkAssignGap,
}

export type AssignedAllocationDisplay =
    | {type: 'single'; allocation: JobAllocation}
    | {
          type: 'group'
          resourceName: string
          count: number
          allocations: JobAllocation[]
          stockQuantity?: boolean
          rentalAssetTypeId?: string
      }

export function isStockEquipmentRequirement(
    requirement: JobRequirement,
    equipment: DispatchResource[],
): boolean {
    if (requirement.kind !== 'rental_asset' || !requirement.rentalAssetTypeId) {
        return false
    }

    const hasUniqueEquipmentForType = equipment.some(
        (asset) =>
            asset.rentalAssetTypeId === requirement.rentalAssetTypeId &&
            asset.isUniquelyIdentified !== false,
    )
    return !hasUniqueEquipmentForType
}

export function maxQuantityForBulkAssign(
    requirement: JobRequirement,
    job: UnassignedJob,
    assignableQuantity: number,
): number {
    return maxQuantityForBulkAssignGap(
        requirement.quantity,
        allocationCountForRequirement(job, requirement.id),
        assignableQuantity,
    )
}

export function stockAssignabilityReason(assignableQuantity: number): string[] {
    return [`${assignableQuantity} available`]
}

/** Summary + per-job lines for held stock shown in the picker before confirm. */
export function stockHeldElsewhereReasons(
    holders: Array<{jobName: string; quantity: number}>,
    options?: {maxJobs?: number},
): string[] {
    const total = holders.reduce((sum, held) => sum + held.quantity, 0)
    if (total <= 0) {
        return []
    }

    const maxJobs = options?.maxJobs ?? 3
    const lines = [`${total} held on other jobs`]
    const shown = holders.slice(0, maxJobs)
    for (const held of shown) {
        lines.push(`${held.jobName} · ${held.quantity}`)
    }
    const remaining = holders.length - shown.length
    if (remaining > 0) {
        lines.push(`+${remaining} more`)
    }
    return lines
}

export function groupAssignedAllocationsForDisplay(
    requirement: JobRequirement,
    job: UnassignedJob,
    allocations: JobAllocation[],
    equipment: DispatchResource[],
): AssignedAllocationDisplay[] {
    if (
        isStockEquipmentRequirement(requirement, equipment) &&
        (requirement.assignedQuantity ?? 0) > 0
    ) {
        return [
            {
                type: 'group',
                resourceName: requirement.label,
                count: requirement.assignedQuantity!,
                allocations: [],
                stockQuantity: true,
                rentalAssetTypeId: requirement.rentalAssetTypeId,
            },
        ]
    }

    return allocations.map((allocation) => ({type: 'single' as const, allocation}))
}
